import React, { useEffect, useMemo, useState } from "react";
import { addToCart, buyNow } from "../../../../redux/action";
import toast from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatCurrencyAmount, getCurrencySymbol } from "../../../../utils/currency";

const normalizeList = (value) => {
    if (Array.isArray(value)) return value;

    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return normalizeList(parsed);
        } catch {
            return [];
        }
    }

    if (value && typeof value === "object") {
        return Object.values(value);
    }

    return [];
};

const getOptionsByTitle = (choiceOptions, title) => {
    const matchedOption = choiceOptions.find(
        (item) =>
            String(item?.title || item?.attribute || item?.name || "")
                .toLowerCase() === title.toLowerCase()
    );

    return normalizeList(matchedOption?.options || matchedOption?.values);
};

const normalizeOptionValue = (value) => {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");
};

const isOptionMatch = (left, right) => {
    return normalizeOptionValue(left) === normalizeOptionValue(right);
};

const DetailsOfProduct = ({ productDataById, userAddress }) => {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const productId = localStorage.getItem("productId");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const guestIdInt = localStorage.getItem("guest_id");
    const cartData = useSelector((state) => state.cartReducer.cart) || [];

    const cartQuantity = cartData.find(
        (item) => Number(item.productId) === Number(productDataById?.id || productId)
    );

    const initialQuantity = cartQuantity ? cartQuantity.quantity + 1 : 1;

    const choiceOptions = useMemo(
        () => normalizeList(productDataById?.choice_options),
        [productDataById]
    );

    const variations = useMemo(
        () => normalizeList(productDataById?.variation),
        [productDataById]
    );

    const mappedSizes = useMemo(() => getOptionsByTitle(choiceOptions, "Size"), [choiceOptions]);
    const mappedShapes = useMemo(() => getOptionsByTitle(choiceOptions, "Shape"), [choiceOptions]);
    const mappedWeights = useMemo(() => getOptionsByTitle(choiceOptions, "Weight"), [choiceOptions]);

    const [selectedSize, setSelectedSize] = useState("");
    const [selectedShape, setSelectedShape] = useState("");
    const [selectedWeight, setSelectedWeight] = useState("");
    const [filteredSku, setFilteredSku] = useState(null);

    useEffect(() => {
        const firstAvailableVariation = variations.find(
            (item) => Number(item?.qty || 0) > 0
        );

        if (firstAvailableVariation) {
            setSelectedSize(firstAvailableVariation?.size || "");
            setSelectedShape(firstAvailableVariation?.shape || "");
            setSelectedWeight(firstAvailableVariation?.weight || "");
            return;
        }

        setSelectedSize(mappedSizes[0] || "");
        setSelectedShape(mappedShapes[0] || "");
        setSelectedWeight(mappedWeights[0] || "");
    }, [mappedSizes, mappedShapes, mappedWeights, variations]);

    useEffect(() => {
        if (!productDataById) return;

        const matchedVariation = variations.find(
            (item) =>
                isOptionMatch(item?.size, selectedSize) &&
                isOptionMatch(item?.shape, selectedShape) &&
                isOptionMatch(item?.weight, selectedWeight)
        );

        setFilteredSku(matchedVariation || null);
    }, [productDataById, selectedSize, selectedShape, selectedWeight, variations]);

    const currentSalePrice =
        filteredSku?.price !== undefined && filteredSku?.price !== null
            ? filteredSku.price
            : productDataById?.unit_price || 0;

    const currentMrp = productDataById?.unit_price || currentSalePrice;
    const currencySymbol = getCurrencySymbol(productDataById);

    const discountPercent =
        productDataById?.discount_type === "flat" && productDataById?.discount
            ? ((productDataById.discount / currentMrp) * 100).toFixed(0)
            : 0;

    const stockStatus = filteredSku
        ? Number(filteredSku?.qty || 0) > 0
        : Number(productDataById?.current_stock || 0) > 0;

    const commonPayload = {
        id: productDataById?.id,
        quantity: initialQuantity,
        choice_1: filteredSku?.size || selectedSize,
        choice_2: filteredSku?.shape || selectedShape,
        choice_3: filteredSku?.weight || selectedWeight,
        guest_id: guestIdInt
    };

    const onAddCart = async () => {
        try {
            await dispatch(
                addToCart({
                    ...commonPayload,
                    quantity: initialQuantity,
                    totalAmount: currentSalePrice * initialQuantity,
                })
            );

            localStorage.setItem("priviousUrl", "/cart");
            navigate("/cart");
        } catch {
            toast.error("Failed adding to cart.");
        }
    };

    const onBuyNow = async () => {
        try {
            const response = await dispatch(
                buyNow({
                    ...commonPayload,
                    quantity: 1,
                    totalAmount: currentSalePrice,
                })
            );

            const buyNowCartId =
                response?.cart_id ||
                response?.in_cart_key ||
                response?.data?.cart_id ||
                response?.data?.in_cart_key ||
                "";

            if (!buyNowCartId) {
                toast.error("Failed adding to cart.");
                return;
            }

            localStorage.setItem("buyNowCartKey", String(buyNowCartId));
            localStorage.setItem("priviousUrl", "/checkout");

            if (!isLoggedIn) {
                navigate("/login");
                return;
            }

            navigate("/checkout");
        } catch {
            toast.error("Error occurred while adding to cart.");
        }
    };

    return (
        <div className="col-lg-7 col-md-12 col-12 scroll_product_content">
            <div className="product_wpeer">
                <h2>{productDataById?.name || ""}</h2>

                <div className="price_inner">
                    <p className="unitPrice">{formatCurrencyAmount(currentSalePrice, productDataById)}</p>

                    {currentMrp > currentSalePrice && <del>{formatCurrencyAmount(currentMrp, productDataById)}</del>}

                    {discountPercent > 0 && (
                        <span className="discount_percent mb-0">
                            ({discountPercent}% off)
                        </span>
                    )}
                </div>

                <div className="product_sku_section">
                    {mappedSizes.length > 0 && (
                        <div className="product_sku_item">
                            <select value={selectedSize} onChange={(e) => setSelectedSize(e.target.value)}>
                                <option value="">Select Size</option>
                                {mappedSizes.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {mappedShapes.length > 0 && (
                        <div className="product_sku_item">
                            <select value={selectedShape} onChange={(e) => setSelectedShape(e.target.value)}>
                                <option value="">Select Shape</option>
                                {mappedShapes.map((shape) => (
                                    <option key={shape} value={shape}>
                                        {shape}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {mappedWeights.length > 0 && (
                        <div className="product_sku_item">
                            <select value={selectedWeight} onChange={(e) => setSelectedWeight(e.target.value)}>
                                <option value="">Select Weight</option>
                                {mappedWeights.map((weight) => (
                                    <option key={weight} value={weight}>
                                        {weight}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="delivery_date">
                    {!stockStatus ? (
                        <>
                            <button className="web_buttons" disabled>Add to Cart</button>
                            <button className="web_buttons" disabled>Buy Now</button>
                            <div className="tooltip">Out of Stock</div>
                        </>
                    ) : (
                        <>
                            <button className="web_buttons" onClick={onAddCart}>Add to Cart</button>
                            <button className="web_buttons" onClick={onBuyNow}>Buy Now</button>
                        </>
                    )}
                    <hr />
                </div>

                <div className="pragaraf_content">
                    <div className="product_info_row">
                        <strong>Stock :</strong>
                        <span>
                            {stockStatus
                                ? filteredSku
                                    ? filteredSku.qty
                                    : productDataById?.current_stock || 0
                                : "Out of stock"}
                        </span>
                    </div>

                    <div className="product_info_row">
                        <strong>Shipping Cost :</strong>
                        <span>{currencySymbol}{productDataById?.shipping_cost || 0}</span>
                    </div>
                </div>

                <div className="mt-4">
                    <div
                        className="short_description"
                        dangerouslySetInnerHTML={{ __html: productDataById?.details || "" }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DetailsOfProduct;
